import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getCurrentUser,
  signIn,
  signOut,
  confirmSignIn,
  fetchAuthSession,
  type AuthUser,
  type SignInOutput,
} from '@aws-amplify/auth'

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
}

type SignInChallenge =
  | { kind: 'none' }
  | { kind: 'mfa-code' }
  | { kind: 'mfa-setup'; sharedSecret: string; setupUri: string }
  | { kind: 'new-password' }

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60_000

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  })
  const [challenge, setChallenge] = useState<SignInChallenge>({ kind: 'none' })
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  async function checkAuth() {
    try {
      const user = await getCurrentUser()
      const session = await fetchAuthSession()
      const hasValidToken = Boolean(session.tokens?.accessToken)
      setState({ user, isAuthenticated: hasValidToken, isLoading: false })
      return hasValidToken
    } catch {
      setState({ user: null, isAuthenticated: false, isLoading: false })
      return false
    }
  }

  async function refreshSession() {
    try {
      await fetchAuthSession({ forceRefresh: true })
    } catch {
      setState({ user: null, isAuthenticated: false, isLoading: false })
    }
  }

  useEffect(() => {
    let cancelled = false

    async function init() {
      try {
        const user = await getCurrentUser()
        const session = await fetchAuthSession()
        const hasValidToken = Boolean(session.tokens?.accessToken)
        if (!cancelled) setState({ user, isAuthenticated: hasValidToken, isLoading: false })
      } catch {
        if (!cancelled) setState({ user: null, isAuthenticated: false, isLoading: false })
      }
    }

    void init()

    refreshTimerRef.current = setInterval(() => {
      void refreshSession()
    }, TOKEN_REFRESH_INTERVAL_MS)

    return () => {
      cancelled = true
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }
    }
  }, [])

  // Routes the SignInOutput nextStep to the right challenge state.
  // Cognito challenges we handle:
  //   DONE → no challenge, fetch session
  //   CONFIRM_SIGN_IN_WITH_TOTP_CODE → user already enrolled, prompt for code
  //   CONTINUE_SIGN_IN_WITH_TOTP_SETUP → first-time TOTP, show QR + prompt for code
  //   CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED → admin-created user, force password reset
  function applyNextStep(result: SignInOutput) {
    const step = result.nextStep.signInStep

    if (result.isSignedIn || step === 'DONE') {
      setChallenge({ kind: 'none' })
      return { done: true as const }
    }

    if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      setChallenge({ kind: 'mfa-code' })
      return { done: false as const, challenge: 'mfa-code' as const }
    }

    if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      // Type narrowing — Amplify only includes totpSetupDetails on this step
      const setupStep = result.nextStep
      if (!('totpSetupDetails' in setupStep)) {
        throw new Error('TOTP setup step missing totpSetupDetails')
      }
      const details = setupStep.totpSetupDetails
      // Call getSetupUri through the object to preserve `this` binding
      const uri = details.getSetupUri('Dead Channel ICE').toString()
      setChallenge({ kind: 'mfa-setup', sharedSecret: details.sharedSecret, setupUri: uri })
      return { done: false as const, challenge: 'mfa-setup' as const }
    }

    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setChallenge({ kind: 'new-password' })
      return { done: false as const, challenge: 'new-password' as const }
    }

    throw new Error(`Unsupported sign-in step: ${step}`)
  }

  const handleSignIn = useCallback(async (username: string, password: string) => {
    const result = await signIn({ username, password })
    const next = applyNextStep(result)
    if (next.done) await checkAuth()
    return next
  }, [])

  const handleConfirmMfa = useCallback(async (code: string) => {
    const result = await confirmSignIn({ challengeResponse: code })
    const next = applyNextStep(result)
    if (next.done) await checkAuth()
    return next
  }, [])

  const handleConfirmNewPassword = useCallback(async (newPassword: string) => {
    const result = await confirmSignIn({ challengeResponse: newPassword })
    const next = applyNextStep(result)
    if (next.done) await checkAuth()
    return next
  }, [])

  const handleSignOut = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
    }
    await signOut()
    setState({ user: null, isAuthenticated: false, isLoading: false })
    setChallenge({ kind: 'none' })
  }, [])

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    challenge,
    signIn: handleSignIn,
    confirmMfa: handleConfirmMfa,
    confirmNewPassword: handleConfirmNewPassword,
    signOut: handleSignOut,
  }
}
